// The Minotaur: a bull-headed raider who guards the maze game's treasure.
// renderAvatar draws the body; the head is swapped for a bull's (horns,
// floppy ears, broad muzzle with a nose ring) and a tufted tail is added.
import { renderAvatar } from "@/lib/avatar2d";

const O = "#2b1f2a";
const st = (fill, w = 4.5) => `fill="${fill}" stroke="${O}" stroke-width="${w}" stroke-linejoin="round" stroke-linecap="round"`;

function horns(p) {
  const L = "M54 66 Q16 60 10 26 Q8 8 20 -6 Q22 18 34 34 Q46 46 66 48 Z";
  const R = `<path d="${L}" transform="translate(200 0) scale(-1 1)" ${st(p.horn)}/>`;
  const tipL = "M10 26 Q8 8 20 -6 Q20 10 24 20 Z";
  return `<path d="${L}" ${st(p.horn)}/>` + R +
    `<path d="${tipL}" fill="${p.hornTip}" stroke="${O}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="${tipL}" transform="translate(200 0) scale(-1 1)" fill="${p.hornTip}" stroke="${O}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M40 56 q-6 -8 -4 -16 M32 50 q-6 -6 -6 -14 M160 56 q6 -8 4 -16 M168 50 q6 -6 6 -14" fill="none" stroke="${O}" stroke-width="2.5" opacity=".35"/>`;
}

function earsAndTuft(p) {
  const ear = "M46 84 Q14 80 4 98 Q22 110 48 100 Z";
  return `<path d="${ear}" ${st(p.fur)}/><path d="${ear}" transform="translate(200 0) scale(-1 1)" ${st(p.fur)}/>` +
    `<path d="M40 88 Q22 90 14 98 Q26 102 42 98 Z" fill="${p.inner}"/>` +
    `<path d="M160 88 Q178 90 186 98 Q174 102 158 98 Z" fill="${p.inner}"/>`;
}

function head(p, mood) {
  const fierce = mood !== "happy";
  const eye = (x, s) =>
    `<ellipse cx="${x}" cy="94" rx="9" ry="10" fill="#fff" stroke="${O}" stroke-width="3.5"/>` +
    `<circle cx="${x + s * 1.5}" cy="96" r="5.5" fill="${p.eye}"/><circle cx="${x + s * 1.5}" cy="96" r="2.6" fill="${O}"/>` +
    `<circle cx="${x + s * 1.5 - 2}" cy="93" r="1.8" fill="#fff"/>`;
  const brows = fierce
    ? `<path d="M60 76 L92 88 L90 80 L64 70 Z M140 76 L108 88 L110 80 L136 70 Z" fill="${p.mane}" stroke="${O}" stroke-width="3.5" stroke-linejoin="round"/>`
    : `<path d="M62 78 q14 -8 28 0 M110 78 q14 -8 28 0" fill="none" stroke="${O}" stroke-width="5" stroke-linecap="round"/>`;
  const muzzle =
    `<path d="M62 116 Q60 96 100 96 Q140 96 138 116 Q140 146 100 148 Q60 146 62 116 Z" ${st(p.muzzle)}/>` +
    `<ellipse cx="84" cy="118" rx="6" ry="7.5" fill="${O}"/><ellipse cx="116" cy="118" rx="6" ry="7.5" fill="${O}"/>` +
    `<path d="M90 132 q10 ${fierce ? -4 : 8} 20 0" fill="none" stroke="${O}" stroke-width="4" stroke-linecap="round"/>` +
    `<path d="M88 124 Q88 146 100 146 Q112 146 112 124" fill="none" stroke="${O}" stroke-width="7" stroke-linecap="round"/>` +
    `<path d="M88 124 Q88 146 100 146 Q112 146 112 124" fill="none" stroke="${p.ring}" stroke-width="3.5" stroke-linecap="round"/>`;
  return `<path d="M46 96 Q40 50 100 44 Q160 50 154 96 Q160 140 100 150 Q40 140 46 96 Z" ${st(p.fur)}/>` +
    `<path d="M100 44 Q160 50 154 96 Q160 140 100 150 Q128 108 100 44 Z" fill="#000" opacity=".09"/>` +
    `<path d="M74 54 Q86 36 100 50 Q114 36 126 54 Q114 62 100 58 Q86 62 74 54 Z" fill="${p.mane}" stroke="${O}" stroke-width="3.5" stroke-linejoin="round"/>` +
    eye(78, 1) + eye(122, -1) + brows + muzzle;
}

function tail(p) {
  return `<path d="M120 206 q34 4 42 -18 q6 -16 18 -18" fill="none" stroke="${O}" stroke-width="11" stroke-linecap="round"/>` +
    `<path d="M120 206 q34 4 42 -18 q6 -16 18 -18" fill="none" stroke="${p.fur}" stroke-width="5" stroke-linecap="round"/>` +
    `<path d="M176 172 q14 -10 22 2 q-4 6 -2 14 q-12 -2 -20 -16z" ${st(p.mane, 3.5)}/>`;
}

const MOOD = { cheer: "happy", taunt: "fierce", ready: "fierce", grab: "fierce", sad: "sad" };

export function renderMinotaur(look, p, opts = {}) {
  let svg = renderAvatar({ ...look, skin: p.fur, ears: "round", hair: "bald", face: "none", headwear: "none" }, opts);
  svg = svg.replace(`<g class="jav-all">`, `<g class="jav-all">${tail(p)}`);
  const h = svg.indexOf(`<g class="jav-head">`) + `<g class="jav-head">`.length;
  const close = svg.indexOf(`</g>`, h);
  return svg.slice(0, h) + horns(p) + earsAndTuft(p) + head(p, MOOD[opts.pose] || "fierce") + svg.slice(close);
}

// Three looks to choose from.
export const MINOTAURS = {
  bronze: {
    name: "Bronze Bull",
    look: { body: "male", outfit: "armour", outfitColour: "#7a5230", trim: "#c98b3a", legColour: "#5b3b2a", bootColour: "#3d2a1e", weapon: "axe", shield: "round", shieldColour: "#b5651d" },
    palette: { fur: "#8a5a3c", mane: "#4a2f1e", muzzle: "#d9b08c", inner: "#e8a0a0", horn: "#f1e6cf", hornTip: "#a89a7e", eye: "#8b4513", ring: "#f2c94c" },
  },
  shadow: {
    name: "Shadow Bull",
    look: { body: "male", outfit: "leather", outfitColour: "#2c2c2c", trim: "#c0392b", legColour: "#2c2c2c", bootColour: "#2c2c2c", weapon: "hammer", shield: "none", shieldColour: "#59616b" },
    palette: { fur: "#3d3a40", mane: "#1d1a1f", muzzle: "#6b6470", inner: "#b0606a", horn: "#d8d2c4", hornTip: "#3a3438", eye: "#e0312a", ring: "#c7ccd1" },
  },
  golden: {
    name: "Labyrinth King",
    look: { body: "male", outfit: "robe", outfitColour: "#7a1f2b", trim: "#f2c94c", legColour: "#3b2a2a", bootColour: "#3d2a1e", weapon: "spear", shield: "kite", shieldColour: "#f2c94c" },
    palette: { fur: "#c08a52", mane: "#7a4a24", muzzle: "#efd2b0", inner: "#f0a8a8", horn: "#f7efe0", hornTip: "#f2c94c", eye: "#2e6f3e", ring: "#f2c94c" },
  },
};
